import type { ModulatorRepository } from "~application/devices/ports/modulator-repository.ts";
import type { SiteRepository } from "~application/sites/ports/site-repository.ts";
import type { ModulatorLinkCsId } from "~domain/devices/models/links/link-model.ts";
import type { ModulatorLastTemperaturePoint } from "~domain/devices/models/modulators/modulator-last-temperature-point-model.ts";
import type { Modulator } from "~domain/devices/models/modulators/modulator-model.ts";
import type { Pilot } from "~domain/devices/models/pilot/pilot-model.ts";
import type { VoltalisEvent } from "~domain/events/models/event-model.ts";
import type { SiteAutodiagDeviceDiagnosis, SiteAutodiagDiagnosis } from "~domain/sites/models/site-autodiag-model.ts";
import type { Site, SiteId } from "~domain/sites/models/site-model.ts";
import type { SiteModulationInfos } from "~domain/sites/models/site-modulation-infos-model.ts";
import type { SitePushButtonEvent } from "~domain/sites/models/site-push-button-event-model.ts";
import type { SiteRecentActions } from "~domain/sites/models/site-recent-actions-model.ts";
import type { SearchSiteResult, SearchSitesPayload } from "~domain/sites/models/site-search-model.ts";
import type { SiteWorkingCondition } from "~domain/sites/models/site-working-condition.ts";
import type { BackofficeSdkClient } from "~infrastructure/api/backoffice/http/backoffice-sdk-client.ts";
import type { BackofficeApi } from "~infrastructure/api/backoffice/http/backoffice-tsrest-client.ts";

import {
  DomainError,
  DomainErrorCode,
  DomainNotFoundError,
  DomainTransportError,
  DomainUnauthorizedError,
} from "~domain/global/errors/domain-error.ts";
import { ESiteConstraint } from "~domain/sites/models/site-constraint-model.ts";
import { getSiteV2 } from "~infrastructure/.generated/hey/backoffice-backend";
import { handleBackofficeError } from "~infrastructure/api/backoffice/http/backoffice-error-handler.ts";
import { ModulatorMapper } from "~infrastructure/devices/mappers/modulator-mapper.ts";
import { PilotMapper } from "~infrastructure/devices/mappers/pilot-mapper.ts";
import { SiteEventMapper } from "~infrastructure/sites/mappers/site-event-mapper.ts";
import { SiteMapper } from "~infrastructure/sites/mappers/site-mapper.ts";
import { isDefined } from "~utils/collection.ts";

export class BackofficeSiteRepository implements SiteRepository {
  constructor(
    private readonly backofficeSdk: BackofficeSdkClient,
    private readonly backofficeApi: BackofficeApi,
    private readonly modulatorRepository: ModulatorRepository,
  ) {}

  async searchSites(payload: SearchSitesPayload): Promise<Array<SearchSiteResult>> {
    const response = await this.backofficeApi.sites.searchSites({
      body: payload,
    });

    if (response.status === 200) {
      return SiteMapper.fromBackofficeSearchSiteToDomain(response.body);
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    if (response.status === 403) {
      throw new DomainUnauthorizedError();
    }

    throw new DomainTransportError(`Unexpected error`);
  }

  async findSite(siteId: SiteId): Promise<Site> {
    const response = await getSiteV2({
      client: this.backofficeSdk.client,
      path: { siteId },
      headers: this.backofficeSdk.headers,
    });

    if (response.error) {
      throw handleBackofficeError(response.error);
    }

    return SiteMapper.fromBackofficeSiteToDomain(response.data);
  }

  async findSiteModulators(site: Site): Promise<Array<Modulator>> {
    const response = await this.backofficeApi.sites.findSiteModulators({
      params: {
        siteId: site.siteId,
      },
    });

    if (response.status === 200) {
      const modulators = ModulatorMapper.mapBackofficeSiteModulatorsToDomain(response.body);

      if (!modulators.length) {
        return modulators;
      }

      let modulatorLastTemperaturePoints: Array<ModulatorLastTemperaturePoint> = [];
      try {
        modulatorLastTemperaturePoints = await this.modulatorRepository.findModulatorsLastTemperaturePoint(modulators);
      } catch {}

      return modulators.map((mod) => {
        const maybeLastTemperaturePoint = modulatorLastTemperaturePoints.find((mltp) => mltp.csId === mod.csId);
        return {
          ...mod,
          lastTemperaturePointDate: maybeLastTemperaturePoint?.date,
        };
      });
    }

    if (response.status === 404) {
      throw new DomainNotFoundError(`Unable to found modulators with site id ${site.siteId}`);
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    if (response.status === 403) {
      throw new DomainUnauthorizedError();
    }

    throw new DomainTransportError(`Unexpected error`);
  }

  async findSitePilots(site: Site): Promise<Array<Pilot>> {
    const [sitePilotsResponse, sitePilotsLastConnectionDatesResponse] = await Promise.all([
      this.backofficeApi.sites.findSitePilots({
        params: {
          siteId: site.siteId,
        },
      }),
      this.backofficeApi.sites.findSitePilotsLastConnectionDates({
        params: {
          siteId: site.siteId,
        },
      }),
    ]);

    if (sitePilotsResponse.status === 200) {
      const pilotResponse = PilotMapper.mapBackofficeSitePilotsToDomain(sitePilotsResponse.body);

      if (sitePilotsLastConnectionDatesResponse.status === 200) {
        return pilotResponse.map((pilot) => {
          const pilotLastConnectionDates = sitePilotsLastConnectionDatesResponse.body.filter(
            (plc) => plc.momboxId === pilot.csId,
          );
          const sortedPilotLastConnectionDates = pilotLastConnectionDates.sort(
            (a, b) => b.lastConnectionDate.getTime() - a.lastConnectionDate.getTime(),
          );
          return {
            ...pilot,
            lastConnectionDate: sortedPilotLastConnectionDates[0]?.lastConnectionDate ?? undefined,
          } satisfies Pilot;
        });
      }

      return pilotResponse;
    }

    if (sitePilotsResponse.status === 404) {
      throw new DomainNotFoundError(`Unable to found pilots with site id ${site.siteId}`);
    }

    if (sitePilotsResponse.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, sitePilotsResponse.body.message);
    }

    if (sitePilotsResponse.status === 403) {
      throw new DomainUnauthorizedError();
    }

    throw new DomainTransportError(`Unexpected error`);
  }

  async findSiteWorkingCondition(site: Site): Promise<SiteWorkingCondition> {
    const response = await this.backofficeApi.sites.findSiteModulatorsWorkingCondition({
      params: {
        siteId: site.siteId,
      },
    });

    if (response.status === 200) {
      return SiteMapper.fromBackofficeSiteWorkingConditionToDomain(response.body);
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    if (response.status === 403) {
      throw new DomainUnauthorizedError();
    }

    throw new DomainTransportError(`Unexpected error`);
  }

  async findSiteModulationInfo(site: Site): Promise<SiteModulationInfos> {
    const response = await this.backofficeApi.sites.findSiteNextModulationAvailability({
      params: { siteId: site.siteId },
    });

    if (response.status === 200) {
      if (response.body instanceof Date) {
        return {
          availableAt: response.body,
          // In the source code of voltexploit, there is an hardcoded 0 ?
          lastThreeDaysModulationsCount: 0,
        };
      }

      return {
        availableAt: undefined,
        // In the source code of voltexploit, there is an hardcoded 0 ?
        lastThreeDaysModulationsCount: 0,
      };
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    throw new DomainTransportError(`Unexpected error`);
  }

  async findSitePushButtonEvents(site: Site): Promise<Array<SitePushButtonEvent>> {
    const response = await this.backofficeApi.sites.findSiteLastButtonEvent({
      params: { siteId: site.siteId },
    });

    if (response.status === 200) {
      return response.body;
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    throw new DomainTransportError(`Unexpected error`);
  }

  async findSiteConstraints(site: Site): Promise<Array<ESiteConstraint>> {
    const response = await this.backofficeApi.sites.findSiteConstraints({
      params: { siteId: site.siteId },
    });

    if (response.status === 200) {
      return [
        response.body.hasDefinitiveDeactivation ? ESiteConstraint.HAS_DEFINITIVE_DEACTIVATION : undefined,
        response.body.hasTemporaryDeactivation ? ESiteConstraint.HAS_TEMPORARY_DEACTIVATION : undefined,
        response.body.hasLinkDeactivation ? ESiteConstraint.HAS_LINK_DEACTIVATION : undefined,
        response.body.hasModulationLimitation ? ESiteConstraint.HAS_MODULATION_LIMITATION : undefined,
        response.body.hasPlanning ? ESiteConstraint.HAS_PLANNING : undefined,
      ].filter(isDefined);
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    throw new DomainTransportError(`Unexpected error`);
  }

  async findSiteRecentActions(site: Site): Promise<SiteRecentActions> {
    const [lastSoftwareUpdateResponse, lastHardwareConfigurationResponse, lastCommunicationResponse] =
      await Promise.allSettled([
        this.backofficeApi.sites.findSiteLastSoftwareUpdate({
          params: { siteId: site.siteId },
        }),
        this.backofficeApi.sites.findSiteLastHardwareConfiguration({
          params: { siteId: site.siteId },
        }),
        this.backofficeApi.sites.findSiteLastCommunication({
          params: { siteId: site.siteId },
        }),
      ]);

    const response: SiteRecentActions = {
      lastCommunication: undefined,
      lastHardwareConfiguration: undefined,
      lastSoftwareUpdate: undefined,
    };

    if (lastSoftwareUpdateResponse.status === "fulfilled") {
      if (lastSoftwareUpdateResponse.value.status === 200) {
        if (lastSoftwareUpdateResponse.value.body && !(lastSoftwareUpdateResponse.value.body instanceof Blob)) {
          response.lastSoftwareUpdate = lastSoftwareUpdateResponse.value.body.datePrepared ?? undefined;
        }
      } else {
        console.error("Error while fetching last-software-update", lastSoftwareUpdateResponse.value);
      }
    } else {
      console.error("Error while fetching last-software-update", lastSoftwareUpdateResponse.reason);
    }

    if (lastHardwareConfigurationResponse.status === "fulfilled") {
      if (lastHardwareConfigurationResponse.value.status === 200) {
        if (
          lastHardwareConfigurationResponse.value.body &&
          !(lastHardwareConfigurationResponse.value.body instanceof Blob)
        ) {
          response.lastHardwareConfiguration = lastHardwareConfigurationResponse.value.body.createDateTime ?? undefined;
        }
      } else {
        console.error("Error while fetching last-hardware-configuration", lastHardwareConfigurationResponse.value);
      }
    } else {
      console.error("Error while fetching last-hardware-configuration", lastHardwareConfigurationResponse.reason);
    }

    if (lastCommunicationResponse.status === "fulfilled") {
      if (lastCommunicationResponse.value.status === 200) {
        if (lastCommunicationResponse.value.body && !(lastCommunicationResponse.value.body instanceof Blob)) {
          response.lastCommunication = lastCommunicationResponse.value.body.createDateTime ?? undefined;
        }
      } else {
        console.error("Error while fetching last-communication", lastCommunicationResponse.value);
      }
    } else {
      console.error("Error while fetching last-communication", lastCommunicationResponse.reason);
    }

    return response;
  }

  async findSiteAutodiagDiagnosis(site: Site): Promise<SiteAutodiagDiagnosis> {
    const response = await this.backofficeApi.sites.findAutodiagDiagnosis({
      params: { siteId: site.siteId },
    });

    if (response.status === 200) {
      return SiteMapper.fromBackofficeAutodiagDiagnosisToDomain(response.body);
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    throw new DomainTransportError(`Unexpected error`);
  }

  async startLinkAutoDiag(
    site: Pick<Site, "siteId">,
    csLinkId: ModulatorLinkCsId,
  ): Promise<SiteAutodiagDeviceDiagnosis> {
    const response = await this.backofficeApi.sites.startLinkAutoDiag({
      params: { siteId: site.siteId, csLinkId: csLinkId },
      body: undefined,
    });

    if (response.status === 200) {
      return SiteMapper.fromBackofficeAutodiagDeviceDiagnosisToDomain(response.body);
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    throw new DomainTransportError(`Unexpected error`);
  }

  async autodiagRefreshAllMyVAppliances(site: Pick<Site, "siteId">): Promise<unknown> {
    const response = await this.backofficeApi.sites.autodiagRefreshAllMyVAppliances({
      params: { siteId: site.siteId },
      body: undefined,
    });

    if (response.status === 200) {
      return;
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    throw new DomainTransportError(`Unexpected error`);
  }

  async findEventTree(site: Pick<Site, "siteId">): Promise<Array<VoltalisEvent>> {
    const response = await this.backofficeApi.sites.findSiteEventTree({
      params: { siteId: site.siteId },
    });

    if (response.status === 200) {
      return SiteEventMapper.fromBackofficeSiteEventsToDomain(response.body);
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    throw new DomainTransportError(`Unexpected error`);
  }
}
